import {
  BestCalculationResult,
  ResultOverviewFile,
} from 'grts-utils/types/ProfitCalcs';
import React, { useCallback, useMemo, useState } from 'react';
import { getTimeFrameIntervalTimestamp } from '../../../../Detail/Charts/PriceChart/helpers';
import { colors } from '../../../../../colors';
import { hasDailyRate, toDailyRatePercent } from '../../../helpers';

interface Props {
  data: ResultOverviewFile | undefined;
  setTimeFrameCombination: (timeFrameCombination: string) => void;
}

const getNormalizedValue = (value: number, min: number, max: number) => {
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
};

const getGreenShadeHSL = (value: number, min: number, max: number) => {
  if (min === max) {
    return 'hsl(120, 100%, 20%)'; // or whichever color you prefer as fallback (dark green here)
  }

  const t = getNormalizedValue(value, min, max);
  // HSL green range:
  // Dark green   ≈ hsl(120, 100%, 20%)
  // Neon green   ≈ hsl(100, 100%, 65%) or hsl(90, 100%, 60%)

  const hueStart = 120;
  const hueEnd = 95; // slightly yellower green for neon feel

  const saturation = 100;
  const lightnessStart = 10;
  const lightnessEnd = 55;

  const h = Math.round(hueStart + t * (hueEnd - hueStart));
  const s = saturation;
  const l = Math.round(lightnessStart + t * (lightnessEnd - lightnessStart));
  return `hsl(${h}, ${s}%, ${l}%)`;
};

const getIntensity = (value: number, min: number, max: number) => {
  // Early return when min === max (avoid division by zero)
  if (min === max) {
    return 0.6;
  }

  // Clamp input value to [min, max]
  const clamped = Math.max(min, Math.min(max, value));

  // Normalize to 0–1
  const t = (clamped - min) / (max - min);

  // Map from 0–1 → 0.6–1
  const output = 0.5 + t * (1 - 0.5);

  return output;
};

const ProfitDistributionChart = ({ data, setTimeFrameCombination }: Props) => {
  const [intervalsToFilterOut, setIntervalsToFilterOut] = useState<
    Array<number>
  >([]);
  const [isDailyRate, setIsDailyRate] = useState(true);

  // A result file is written by one run, so its cells share one unit. Files from
  // before the rate switch store baseScore only — the whole grid then falls back
  // to it, which keeps a single unit on the colour scale.
  const usesDailyRate = useMemo(() => {
    if (hasDailyRate(data?.bestResult)) {
      return true;
    }

    return Object.values(data?.timeFrames ?? {}).some((arrayOfTimeFrames) =>
      Object.values(arrayOfTimeFrames ?? {}).some((overview) =>
        hasDailyRate(overview?.bestResult),
      ),
    );
  }, [data?.bestResult, data?.timeFrames]);

  const getValue = useCallback(
    (result: BestCalculationResult | null | undefined) => {
      if (!result) {
        return 0;
      }

      if (!isDailyRate) {
        return result.score;
      }

      return usesDailyRate
        ? toDailyRatePercent(result.growthRate ?? 0)
        : result.baseScore;
    },
    [isDailyRate, usesDailyRate],
  );

  // Rates carry a % suffix; a legacy baseScore fallback is labelled so it is
  // never mistaken for one.
  const formatValue = useCallback(
    (value: number) => {
      if (!isDailyRate) {
        return value.toFixed(4);
      }

      return usesDailyRate
        ? `${value.toFixed(4)} %`
        : `${value.toFixed(4)} base`;
    },
    [isDailyRate, usesDailyRate],
  );

  const bestResult = useMemo(() => data?.bestResult, [data?.bestResult]);
  const bestResultTimeFrameCombination = useMemo(() => {
    if (!bestResult) {
      return '';
    }

    const higherTimeFrame = bestResult?.params.higherTimeFrame;
    const shorterTimeFrame = bestResult?.params.shorterTimeFrame;
    const multiOrderTimeFrame = bestResult?.params.multiOrderTimeFrame;

    return `${higherTimeFrame} ${shorterTimeFrame} ${multiOrderTimeFrame}`;
  }, [bestResult]);
  const bestResultValue = useMemo(() => {
    return getValue(bestResult);
  }, [getValue, bestResult]);

  const [hoverResult, setHoverResult] = useState<
    BestCalculationResult | undefined
  >();

  const rows = useMemo(
    () => (data?.timeFrames ? Object.keys(data.timeFrames).length : 0),
    [data?.timeFrames],
  );

  const grid = useMemo(() => {
    if (!data?.timeFrames) {
      return undefined;
    }

    const results: BestCalculationResult[][] = new Array(rows);
    const timeFrames = Object.keys(data.timeFrames);

    timeFrames.forEach((timeFrame, rowIndex) => {
      results[rowIndex] = [];
      // timeframe is row index
      const timeFrameCombinations = data.timeFrames?.[timeFrame]
        ? Object.keys(data.timeFrames?.[timeFrame])
        : [];

      timeFrameCombinations.forEach((timeFrameCombination) => {
        const higherTimeFrame =
          data.timeFrames?.[timeFrame]?.[timeFrameCombination]?.bestResult
            ?.params.higherTimeFrame;
        const shorterTimeFrame =
          data.timeFrames?.[timeFrame]?.[timeFrameCombination]?.bestResult
            ?.params.shorterTimeFrame;
        const multiOrderTimeFrame =
          data.timeFrames?.[timeFrame]?.[timeFrameCombination]?.bestResult
            ?.params.multiOrderTimeFrame;

        if (!higherTimeFrame || !shorterTimeFrame || !multiOrderTimeFrame) {
          return;
        }

        const higherTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(higherTimeFrame);
        const shorterTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(shorterTimeFrame);
        const multiTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(multiOrderTimeFrame);
        const columnIndex =
          higherTIntervalTimestamp / shorterTIntervalTimestamp;
        const multiTInterval =
          shorterTIntervalTimestamp / multiTIntervalTimestamp;

        if (
          intervalsToFilterOut.includes(columnIndex) ||
          intervalsToFilterOut.includes(multiTInterval)
        ) {
          return;
        }

        // @TODO don't pick all results, but have columns inside a column (for the multiTInterval)
        if (data.timeFrames?.[timeFrame]?.[timeFrameCombination]?.bestResult) {
          if (!results[rowIndex][columnIndex]) {
            results[rowIndex][columnIndex] =
              data.timeFrames?.[timeFrame]?.[timeFrameCombination]?.bestResult;
          } else {
            if (
              getValue(
                data.timeFrames?.[timeFrame]?.[timeFrameCombination]
                  ?.bestResult,
              ) > getValue(results[rowIndex][columnIndex])
            ) {
              results[rowIndex][columnIndex] =
                data.timeFrames?.[timeFrame]?.[
                  timeFrameCombination
                ]?.bestResult;
            }
          }
        }
      });
    });

    return results;
  }, [data?.timeFrames, getValue, intervalsToFilterOut, rows]);

  const { min, max } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    if (!data?.timeFrames) {
      return { min: 0, max: 1 };
    }

    Object.values(data?.timeFrames).forEach((arrayOfTimeFrames) => {
      Object.values(arrayOfTimeFrames).forEach((overview) => {
        const result = overview?.bestResult;
        const value = getValue(result);
        min = Math.min(min, value ?? min);
        max = Math.max(max, value ?? max);
      });
    });

    return { min, max };
  }, [data?.timeFrames, getValue]);

  const { minGrid, maxGrid } = useMemo(() => {
    let minGrid = Infinity;
    let maxGrid = -Infinity;

    if (!data?.timeFrames) {
      return { minGrid: 0, maxGrid: 1 };
    }

    Object.values(data?.timeFrames).forEach((arrayOfTimeFrames) => {
      Object.values(arrayOfTimeFrames).forEach((overview) => {
        const result = overview?.bestResult;

        const higherTimeFrame = result?.params.higherTimeFrame;
        const shorterTimeFrame = result?.params.shorterTimeFrame;
        const multiOrderTimeFrame = result?.params.multiOrderTimeFrame;

        if (!higherTimeFrame || !shorterTimeFrame || !multiOrderTimeFrame) {
          return;
        }
        const higherTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(higherTimeFrame);
        const shorterTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(shorterTimeFrame);
        const shorterTInterval =
          higherTIntervalTimestamp / shorterTIntervalTimestamp;
        const multiTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(multiOrderTimeFrame);
        const multiTInterval =
          shorterTIntervalTimestamp / multiTIntervalTimestamp;

        if (
          !intervalsToFilterOut.includes(shorterTInterval) &&
          !intervalsToFilterOut.includes(multiTInterval)
        ) {
          const value = getValue(result);
          minGrid = Math.min(minGrid, value ?? minGrid);
          maxGrid = Math.max(maxGrid, value ?? maxGrid);
        }
      });
    });

    return { minGrid, maxGrid };
  }, [data?.timeFrames, getValue, intervalsToFilterOut]);

  const xAxisKeys = useMemo(() => {
    const keys: number[] = [];

    if (!data?.timeFrames) {
      return keys;
    }

    Object.values(data?.timeFrames).forEach((arrayOfTimeFrames) => {
      Object.values(arrayOfTimeFrames).forEach((overview) => {
        const result = overview?.bestResult;
        const higherTimeFrame = result?.params.higherTimeFrame;
        const shorterTimeFrame = result?.params.shorterTimeFrame;

        if (!higherTimeFrame || !shorterTimeFrame) {
          return;
        }

        const higherTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(higherTimeFrame);
        const shorterTIntervalTimestamp =
          getTimeFrameIntervalTimestamp(shorterTimeFrame);
        const shorterTInterval =
          higherTIntervalTimestamp / shorterTIntervalTimestamp;

        if (!keys.includes(shorterTInterval)) {
          keys.push(shorterTInterval);
        }
      });
    });

    keys.sort((a, b) => a - b);

    return keys;
  }, [data?.timeFrames]);

  const yAxisKeys = useMemo(() => {
    return data?.timeFrames ? Object.keys(data?.timeFrames) : [];
  }, [data?.timeFrames]);

  const handleIntervalClick = useCallback((interval: number) => {
    setIntervalsToFilterOut((prev) => {
      const updated = [...prev];

      const index = updated.findIndex((i) => i === interval);
      if (index === -1) {
        updated.push(interval);
      } else {
        updated.splice(index, 1);
      }

      return updated;
    });
  }, []);

  const hoverTimeFrameCombination = useMemo(() => {
    if (!hoverResult) {
      return '';
    }

    const higherTimeFrame = hoverResult?.params.higherTimeFrame;
    const shorterTimeFrame = hoverResult?.params.shorterTimeFrame;
    const multiOrderTimeFrame = hoverResult?.params.multiOrderTimeFrame;

    return `${higherTimeFrame} ${shorterTimeFrame} ${multiOrderTimeFrame}`;
  }, [hoverResult]);

  const hoverValue = useMemo(() => {
    return getValue(hoverResult);
  }, [getValue, hoverResult]);

  const totalDurationLabel = useMemo(() => {
    const duration = hoverResult?.duration ?? data?.duration;

    if (!duration) {
      return undefined;
    }

    const amountOfHours = Math.floor(duration / (1000 * 60 * 60));
    const amountOfMinutes = Math.floor(
      (duration % (1000 * 60 * 60)) / (1000 * 60),
    );

    return `${amountOfHours}h ${amountOfMinutes}m`;
  }, [data?.duration, hoverResult?.duration]);

  if (!data) {
    return;
  }

  return (
    <div
      style={{
        display: 'flex',
        margin: 32,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateRows: `repeat(${rows + 1}, 1fr)`,
          gridTemplateColumns: `repeat(${xAxisKeys.length + 1}, 1fr)`,
          gap: 6,
          height: 'fit-content',
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span style={{ color: 'white', fontWeight: 'bold' }}></span>
        </div>
        {xAxisKeys.map((key) => (
          <div
            key={key}
            style={{
              width: 20,
              height: 20,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => handleIntervalClick(key)}
          >
            <span
              style={{
                color: intervalsToFilterOut.includes(key) ? 'gray' : 'white',
                fontWeight: intervalsToFilterOut.includes(key)
                  ? 'normal'
                  : 'bold',
              }}
            >
              {key}
            </span>
          </div>
        ))}
        {grid?.map((row, rowIndex) => (
          <>
            <div
              key={`${rowIndex}-${-1}`}
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <span style={{ color: 'white', fontWeight: 'bold' }}>
                {yAxisKeys[rowIndex]}
              </span>
            </div>
            {xAxisKeys.map((key) => {
              if (row[key]) {
                return (
                  <div
                    key={`${rowIndex}-${key}`}
                    style={{
                      backgroundColor: getGreenShadeHSL(
                        getValue(row[key]),
                        min,
                        max,
                      ),
                      width: 20,
                      height: 20,
                      transform: `scale(${getIntensity(
                        getValue(row[key]),
                        min,
                        max,
                      )})`,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => setHoverResult(row[key])}
                    onMouseOut={() => setHoverResult(undefined)}
                    onClick={() =>
                      setTimeFrameCombination(
                        `${row[key].params.higherTimeFrame}_${row[key].params.shorterTimeFrame}_${row[key].params.multiOrderTimeFrame}`,
                      )
                    }
                  ></div>
                );
              }

              return (
                <div
                  key={`${rowIndex}-${key}`}
                  style={{
                    backgroundColor: 'gray',
                    width: 20,
                    height: 20,
                    transform: `scale(${getIntensity(min, min, max)})`,
                  }}
                />
              );
            })}
          </>
        ))}
      </div>

      <div style={{ marginLeft: 24 }}>
        <div
          style={{
            display: 'flex',
            color: 'white',
            gap: 8,
          }}
        >
          <div
            style={{
              padding: 2,
              paddingLeft: 6,
              paddingRight: 6,
              backgroundColor: colors.gray,
              borderRadius: 6,
              fontSize: 12,
              cursor: isDailyRate ? 'default' : 'pointer',
              opacity: isDailyRate ? 1 : 0.5,
            }}
            onClick={() => setIsDailyRate(true)}
          >
            {usesDailyRate ? 'Daily Rate' : 'Base Score'}
          </div>
          <div
            style={{
              padding: 2,
              paddingLeft: 6,
              paddingRight: 6,
              backgroundColor: colors.gray,
              borderRadius: 6,
              fontSize: 12,
              cursor: !isDailyRate ? 'default' : 'pointer',
              opacity: !isDailyRate ? 1 : 0.5,
            }}
            onClick={() => setIsDailyRate(false)}
          >
            Score
          </div>
        </div>

        {isDailyRate && !usesDailyRate && (
          <div style={{ color: colors.orange, fontSize: 11, marginTop: 6 }}>
            Legacy file — no daily rate recorded, showing base score.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            color: 'white',
            width: '100%',
            marginTop: 8,
            columnGap: 16,
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 'bold' }}>MAX</p>
            <p style={{ margin: 0, marginTop: -4 }}>{formatValue(maxGrid)}</p>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 'bold' }}>MIN</p>
            <p style={{ margin: 0, marginTop: -4 }}>{formatValue(minGrid)}</p>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            color: 'white',
            width: '100%',
            justifyContent: 'space-between',
            marginTop: 8,
            columnGap: 16,
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 'bold' }}>
              {hoverResult ? 'HOVER RESULT' : 'BEST RESULT'}
            </p>
            <p style={{ margin: 0, marginTop: -4 }}>
              {hoverResult
                ? hoverTimeFrameCombination
                : bestResultTimeFrameCombination}
            </p>
            <p style={{ margin: 0, marginTop: -4 }}>
              {formatValue(hoverResult ? hoverValue : bestResultValue)}
            </p>
          </div>
          {totalDurationLabel && (
            <div>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 'bold' }}>
                {hoverResult
                  ? 'TIMEFRAME COMBINATION DURATION'
                  : 'TOTAL DURATION'}
              </p>
              <p style={{ margin: 0, marginTop: -4 }}>{totalDurationLabel}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfitDistributionChart;

/**
 * @TODO
 * A column could still be divided into multiT interval for a better overview
 */

/**
 *
 * Mini conclusion, keeping only the shorterT intervals of 4 6 8 12 covers a wide range
 * But does that mean we can eleminate all the rest already, also multiT interval
 * 6 8 12 also still looks good
 */
